import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, X, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Input con autocompletado para Origen o Destino.
 * - Muestra sugerencias históricas al enfocar / escribir.
 * - X en el input limpia el valor.
 * - X en cada sugerencia la oculta del historial (llamada al backend).
 *
 * Props:
 *  - tipo: 'origen' | 'destino'
 *  - value: string
 *  - onChange(newValue)
 *  - placeholder
 *  - iconColor (tailwind text color)
 *  - testId
 */
export const LugarAutocomplete = ({ tipo, value, onChange, placeholder, iconColor = 'text-green-600', testId }) => {
  const [sugerencias, setSugerencias] = useState([]);
  const [showSugerencias, setShowSugerencias] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const cargar = useCallback(async (q) => {
    setLoading(true);
    try {
      const response = await api.get('/servicios/autocomplete-lugares', {
        params: { tipo, q: q || '' }
      });
      setSugerencias(response.data || []);
    } catch (e) {
      console.error('Error autocomplete-lugares:', e);
    } finally {
      setLoading(false);
    }
  }, [tipo]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    setShowSugerencias(true);
    if (sugerencias.length === 0) {
      cargar(value);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setShowSugerencias(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cargar(v), 250);
  };

  const handleSelect = (sug) => {
    onChange(sug);
    setShowSugerencias(false);
  };

  const handleClear = () => {
    onChange('');
    // Reabrir dropdown con todos los lugares
    cargar('');
    setShowSugerencias(true);
  };

  const handleOcultar = async (e, sug) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm(`¿Ocultar "${sug}" del autocompletado?\n\nEsto no borra los servicios existentes, solo evita que aparezca en las sugerencias futuras.`)) {
      return;
    }
    try {
      await api.post('/servicios/autocomplete-lugares/ocultar', { tipo, valor: sug });
      setSugerencias(prev => prev.filter(s => s !== sug));
      toast.success(`"${sug}" ocultado del autocompletado`);
    } catch (err) {
      toast.error('No se pudo ocultar la sugerencia');
    }
  };

  const sugerenciasFiltradas = value?.trim()
    ? sugerencias.filter(s => s.toLowerCase().includes(value.toLowerCase()))
    : sugerencias;

  return (
    <div ref={containerRef} className="relative">
      <MapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor} z-10`} size={16} />
      <Input
        value={value || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        className="pl-9 pr-9 h-9"
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-700 z-10"
          title="Borrar"
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <X size={14} />
        </button>
      )}

      {showSugerencias && sugerenciasFiltradas.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
          {sugerenciasFiltradas.map((sug) => (
            <div
              key={sug}
              className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 group"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(sug); }}
            >
              <span className="text-sm text-gray-800 truncate flex-1">{sug}</span>
              <button
                type="button"
                onMouseDown={(e) => handleOcultar(e, sug)}
                className="ml-2 text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title={`Eliminar "${sug}" del historial de autocompletado`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {showSugerencias && !loading && sugerenciasFiltradas.length === 0 && value?.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-xs text-gray-500">
          Sin coincidencias — se registrará como nueva dirección
        </div>
      )}
    </div>
  );
};

export default LugarAutocomplete;
